// app/javascript/landing/components/Navbar.tsx
import React, { useState } from "react";
import clsx from "clsx";
import { useScrollHeader } from "../hooks/useScrollHeader";

export type RouteKey = "inicio" | "somos" | "portafolio" | "contacto";

type NavbarProps = {
  currentRoute?: RouteKey;
  onNavigate?: (route: RouteKey, href: string) => void;
};

/** Rutas donde el navbar debe ser transparente sobre el hero */
const OVERLAY_ROUTES: Partial<Record<RouteKey, true>> = {
  inicio: true,
  portafolio: true,
  contacto: true,
};

const NAV_ITEMS: { label: string; href: string; key: RouteKey }[] = [
  { label: "Inicio", href: "/", key: "inicio" },
  { label: "Somos", href: "/somos", key: "somos" },
  { label: "Portafolio", href: "/portafolio", key: "portafolio" },
  { label: "Contacto", href: "/contacto", key: "contacto" },
];

export function Navbar({ currentRoute, onNavigate }: NavbarProps) {
  const { atTop } = useScrollHeader();
  const [menuOpen, setMenuOpen] = useState(false);

  const mode =
    currentRoute && OVERLAY_ROUTES[currentRoute] ? "overlay" : "solid";

  const positionClass =
    mode === "overlay" ? "fixed inset-x-0 top-0" : "fixed top-0";

  const bgClass =
    mode === "overlay"
      ? atTop
        ? "bg-transparent border-transparent"
        : "bg-black shadow"
      : "bg-black shadow";

  const lineBase =
    "pointer-events-none absolute -top-1 left-0 right-0 h-0.5 rounded-full bg-white origin-center transition-transform duration-300";

  const linkClass = (isActive: boolean) =>
    clsx(
      "relative group py-2 min-w-20 text-center text-sm font-bold tracking-wide transition-colors",
      "hover:text-white",
      isActive ? "text-white" : "text-white/70"
    );

  const handleClick =
    (key: RouteKey, href: string) =>
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (onNavigate) {
        event.preventDefault();
        onNavigate(key, href);
      }
    };

  return (
    <header
      className={clsx(
        positionClass,
        "z-40 w-full border-b transition-colors duration-300",
        bgClass
      )}
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-6">
        {/* LOGO */}
        <a href="/" className="flex items-center gap-3">
          <img
            src="/landing/logos/logo-metaverso.png"
            alt="Metaverso de la Construcción"
            className="h-16 w-auto"
          />
        </a>

        {/* MENÚ DESKTOP */}
        <nav className="hidden md:flex gap-8">
          {NAV_ITEMS.map(({ label, href, key }) => {
            const isActive = currentRoute === key;
            return (
              <a
                key={key}
                href={href}
                className={linkClass(isActive)}
                onClick={handleClick(key, href)}
              >
                {label}
                <span
                  aria-hidden="true"
                  className={clsx(
                    lineBase,
                    isActive
                      ? "scale-x-100"
                      : "scale-x-0 group-hover:scale-x-100"
                  )}
                />
              </a>
            );
          })}
        </nav>

        {/* BOTÓN MENÚ MOBILE */}
        <button
          type="button"
          className="group inline-flex md:hidden w-11 h-11 text-white bg-purple-3 text-center items-center justify-center rounded-full  hover:bg-purple-3/90 transition"
          aria-pressed={menuOpen}
          aria-expanded={menuOpen}
          aria-label="Abrir menú"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg
            className="w-6 h-6 fill-current pointer-events-none"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              className="origin-center -translate-y-[5px] translate-x-[7px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-[[aria-pressed=true]]:translate-x-0 group-[[aria-pressed=true]]:translate-y-0 group-[[aria-pressed=true]]:rotate-[315deg]"
              y="7"
              width="9"
              height="2"
              rx="1"
            />
            <rect
              className="origin-center transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.8)] group-[[aria-pressed=true]]:rotate-45"
              y="7"
              width="16"
              height="2"
              rx="1"
            />
            <rect
              className="origin-center translate-y-[5px] transition-all duration-300 ease-[cubic-bezier(.5,.85,.25,1.1)] group-[[aria-pressed=true]]:translate-y-0 group-[[aria-pressed=true]]:-rotate-[225deg]"
              y="7"
              width="9"
              height="2"
              rx="1"
            />
          </svg>
        </button>
      </div>

      {/* MENÚ MOBILE DESPLEGABLE */}
      {menuOpen && (
        <div className="md:hidden absolute inset-x-0 top-full px-4 pt-4 pb-4">
          <nav className="md:hidden bg-purple-3 rounded-2xl shadow border-white/10">
            <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
              {NAV_ITEMS.map(({ label, href, key }) => {
                const isActive = currentRoute === key;
                return (
                  <a
                    key={key}
                    href={href}
                    className={clsx(
                      "relative block py-2 pe-2 ps-7 text-sm font-semibold",
                      isActive ? "text-white" : "text-white/70 hover:text-white"
                    )}
                    onClick={(event) => {
                      if (onNavigate) {
                        event.preventDefault();
                        onNavigate(key, href);
                      }
                      setMenuOpen(false);
                    }}
                  >
                    {label}
                    <span
                      aria-hidden="true"
                      className={clsx(
                        "pointer-events-none absolute top-1/2 -translate-y-1/2 left-0 h-2 w-5 rounded-full bg-white origin-center transition-transform duration-300",
                        isActive ? "scale-x-100" : "scale-x-0"
                      )}
                    />
                  </a>
                );
              })}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
